import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from "react-router-dom";

const ReceivableReport = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [showUncategorized, setShowUncategorized] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });

  const lastFetchedParamsRef = useRef('');

  // Fetch transactions from API
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      let params = {};

      // Add filters
      if (searchQuery) params.search = searchQuery;
      if (bankFilter) params.account_id = bankFilter;
      if (transactionTypeFilter) params.type = transactionTypeFilter;
      if (showUncategorized) params.uncategorised = true;
      if (dateRange.from) params.from = dateRange.from;
      if (dateRange.to) params.to = dateRange.to;

      // Prevent duplicate API calls
      const paramsString = JSON.stringify(params);
      if (lastFetchedParamsRef.current === paramsString) {
        setLoading(false);
        return;
      }
      lastFetchedParamsRef.current = paramsString;

      const response = await axios.get(`${backendUrl}/api/transactions?type=income`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
        params,
      });

      const data = response.data.data;


      setTransactions(data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate report data from transactions
  const calculateReceivablesData = (transactions) => {
    return transactions
      .filter(transaction => transaction.type === 'income')
      .map(transaction => {
        const invoice = transaction.invoice;
        const quotation = invoice?.quotation;
        const client = transaction.client;
        const project = transaction.project || quotation?.project;

        const invoiceAmount = parseFloat(invoice?.amount);
        const amountReceived = parseFloat(transaction.amount || 0);
        const bankCharges = parseFloat(transaction.deduction_amount || 0);
        const netReceived = amountReceived - bankCharges;

        // Calculate pending amount from invoice balance or difference
        const pending = parseFloat((invoiceAmount - amountReceived));

        // Determine status based on amounts
        let status = 'pending';
        if (pending <= 0) {
          status = 'completed';
        } else if (amountReceived > 0 && pending > 0) {
          status = 'partially paid';
        }

        return {
          client: client?.name || 'N/A',
          project: project?.name || quotation?.project?.name || '',
          invoiceNo: invoice?.serial_no || invoice?.ref_no || '',
          invoiceAmount: invoiceAmount,
          amountReceived: amountReceived,
          bankCharges: bankCharges,
          netReceived: netReceived,
          pending: pending > 0 ? pending : 0,
          paymentDate: transaction.date,
          status: status,
          clientId: transaction.client_id,
          proformainvoiceId: invoice?.id || null,

        };
      });
  };

  const reportData = calculateReceivablesData(transactions);

  // Calculate totals
  const totals = reportData.reduce((acc, row) => ({
    invoiceAmount: acc.invoiceAmount + row.invoiceAmount,
    amountReceived: acc.amountReceived + row.amountReceived,
    bankCharges: acc.bankCharges + row.bankCharges,
    netReceived: acc.netReceived + row.netReceived,
    pending: acc.pending + row.pending
  }), {
    invoiceAmount: 0,
    amountReceived: 0,
    bankCharges: 0,
    netReceived: 0,
    pending: 0
  });

  // Handle filter changes
  const handleFilterChange = () => {
    fetchTransactions(1);
  };

  // Handle search
  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      fetchTransactions(1);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setBankFilter('');
    setTransactionTypeFilter('');
    setShowUncategorized(false);
    setDateRange({ from: '', to: '' });
    setTimeout(() => fetchTransactions(1), 100);
  };

  // Initial load
  useEffect(() => {
    fetchTransactions(1);
  }, []);

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-0 font-size-18">Accounts Receivable Report</h4>
                  <ol className="breadcrumb m-0 mt-2">

                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Card */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Filters</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-xl-3 col-md-4 col-sm-6">
                  <label className="form-label">Search</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search clients, projects..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleSearch}
                    />
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={handleSearch}
                    >
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </div>




                <div className="col-xl-2 col-md-4 col-sm-6">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.from}
                    onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                  />
                </div>

                <div className="col-xl-2 col-md-4 col-sm-6">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.to}
                    onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                  />
                </div>



                <div className="col-12">
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={handleFilterChange}
                    >
                      Apply Filters
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={clearFilters}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          {!loading && reportData.length > 0 && (
            <div className="row mt-3">
              <div className="col-xl-2 col-md-4 col-sm-6">
                <div className="card card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="mb-0">{reportData.length}</h4>
                      <span className="text-muted">Total Invoices</span>
                    </div>
                    <div className="flex-shrink-0">
                      <i className="fas fa-file-invoice text-primary h4 mb-0"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-2 col-md-4 col-sm-6">
                <div className="card card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="mb-0">{totals.invoiceAmount.toFixed(2)}</h4>
                      <span className="text-muted">Total Amount </span>
                    </div>
                    <div className="flex-shrink-0">
                      <i className="fas fa-money-bill-wave text-success h4 mb-0"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-2 col-md-4 col-sm-6">
                <div className="card card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="mb-0">{totals.amountReceived.toFixed(2)}</h4>
                      <span className="text-muted">Received </span>
                    </div>
                    <div className="flex-shrink-0">
                      <i className="fas fa-download text-info h4 mb-0"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-2 col-md-4 col-sm-6">
                <div className="card card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="mb-0 text-danger">{totals.pending.toFixed(2)}</h4>
                      <span className="text-muted">Pending</span>
                    </div>
                    <div className="flex-shrink-0">
                      <i className="fas fa-clock text-danger h4 mb-0"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-2 col-md-4 col-sm-6">
                <div className="card card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="mb-0">
                        {totals.invoiceAmount > 0
                          ? ((totals.amountReceived / totals.invoiceAmount) * 100).toFixed(1)
                          : '0'}%
                      </h4>
                      <span className="text-muted">Completion Rate</span>
                    </div>
                    <div className="flex-shrink-0">
                      <i className="fas fa-percentage text-warning h4 mb-0"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-2 col-md-4 col-sm-6">
                <div className="card card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="mb-0">{totals.netReceived.toFixed(2)}</h4>
                      <span className="text-muted">Net Received</span>
                    </div>
                    <div className="flex-shrink-0">
                      <i className="fas fa-hand-holding-usd text-success h4 mb-0"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Report Table Card */}
          <div className="card mt-3">
            <div className="card-header">
              <h5 className="card-title mb-0">Receivables Details</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading receivables data...</p>
                </div>
              ) : reportData.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No receivables data found</h5>
                  <p className="text-muted">Try adjusting your filters or check your data source</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-centered table-nowrap table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Client</th>
                          <th>Project</th>
                          <th>Invoice No</th>
                          <th className="text-end">Invoice Amount </th>
                          <th className="text-end">Amount Received </th>
                          <th className="text-end">Bank Charges </th>
                          <th className="text-end">Net Received </th>
                          <th className="text-end">Pending </th>
                          <th>Payment Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((row, index) => (
                          <tr key={index}>
                            <td>
                              <span className="fw-medium">{row.client}</span>
                            </td>
                            <td>{row.project}</td>
                            <td>{row.proformainvoiceId ? (
                              <Link
                                to={`/proformainvoice/details/${row.proformainvoiceId}`}
                                className="text-decoration-none"
                              >
                                {row.invoiceNo}
                              </Link>
                            ) : (
                              <span className='text-black'>{row.invoiceNo}</span>
                            )}

                            </td>
                            <td className="text-end">{row.invoiceAmount.toFixed(2)}</td>
                            <td className="text-end">{row.amountReceived.toFixed(2)}</td>
                            <td className="text-end">{row.bankCharges.toFixed(2)}</td>
                            <td className="text-end">{row.netReceived.toFixed(2)}</td>
                            <td className="text-end">
                              <span className={`fw-medium ${row.pending > 0 ? 'text-danger' : 'text-success'}`}>
                                {row.pending.toFixed(2)}
                              </span>
                            </td>
                            <td>{row.paymentDate}</td>
                            <td>
                              <span className={`badge ${row.status === 'completed'
                                  ? 'bg-success'
                                  : row.status === 'partially paid'
                                    ? 'bg-warning'
                                    : 'bg-danger'
                                }`}>
                                {row.status.toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3" className="fw-semibold text-end">TOTALS:</td>
                          <td className="text-end fw-semibold">{totals.invoiceAmount.toFixed(2)}</td>
                          <td className="text-end fw-semibold">{totals.amountReceived.toFixed(2)}</td>
                          <td className="text-end fw-semibold">{totals.bankCharges.toFixed(2)}</td>
                          <td className="text-end fw-semibold">{totals.netReceived.toFixed(2)}</td>
                          <td className="text-end fw-semibold text-danger">{totals.pending.toFixed(2)}</td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pageCount > 1 && (
                    <div className="row mt-3">
                      <div className="col-sm-12">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="text-muted">
                              Page {currentPage + 1} of {pageCount}
                            </span>
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => fetchTransactions(currentPage)}
                              disabled={currentPage === 0}
                            >
                              Previous
                            </button>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => fetchTransactions(currentPage + 2)}
                              disabled={currentPage + 1 >= pageCount}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceivableReport;